<template>
  <div>
    <p>Please select <strong><u>ONE</u></strong> of the following methods to search by</p>
    <div class="row">
      <div class="col-md-4">
        <el-input
          type="search"
          style="width: 100%;"
          class="inline-input"
          clearable
          @keyup.enter.native="fetchResults"
          placeholder="Search for an energy plan named ..."
          v-model="searchQuery"
          aria-controls="datatables" />
      </div>
      <div class="col-md-4">
        <el-autocomplete
          style="width:100%"
          class="inline-input"
          ref="planNameId"
          name="planName"
          clearable
          v-model="selectedPlanName"
          :fetch-suggestions="findPlanNames"
          :trigger-on-focus="false"
          value-key="name"
          @blur="verifyPlanNameAutocompleteSelected"
          placeholder="Plan Name or Number"
          @select="setPlanName">
        </el-autocomplete>
        <small class="text-danger" v-show="showInvalidAutocompleteWarning('planNameId')">
          {{ fieldNotValidError('Plan Name') }}
        </small>
      </div>
      <div class="col-md-4">
        <el-autocomplete
          style="width:100%"
          ref="builderId"
          name="builder"
          class="inline-input"
          clearable
          v-model="selectedBuilder.name"
          :fetch-suggestions="findBuilders"
          :trigger-on-focus="false"
          value="builderId"
          value-key="name"
          @blur="verifyBuilderAutocompleteSelected"
          placeholder="Builder"
          @select="setBuilder">
        </el-autocomplete>
        <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
          {{ fieldNotValidError('Builder') }}
        </small>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <button type="button" @click="fetchResults" style="float: right" class="btn btn-fill btn-info">
          Search
        </button>
        <button type="button" @click="clearResults" style="float: right" class="btn btn-fill btn-info">
          Clear
        </button>
        <button type="button" @click="fetchAll" style="float: right" class="btn btn-fill btn-info">
          View All
        </button>
      </div>
    </div>
    <el-table class="table-striped indexed-table-column-width"
              :data="tableData"
              border
              style="width: 100%"
              :max-height="tableHeight(300)">
      <el-table-column
        :width="indexColumnWidth"
        align="center"
        label="#"
        type="index">
      </el-table-column>
      <el-table-column
        label="Name"
        sortable
        :sort-method="(a, b) => callStringSort(a, b, 'name')"
        min-width="200">
        <template slot-scope="props">
          <span class="invisible">{{ props.row.id }}</span>
          <a href="#" @click.prevent="showFormDetails(props.row)">{{ props.row.name }}</a>
        </template>
      </el-table-column>
      <el-table-column
        label="Uploaded By"
        sortable
        :sort-method="(a, b) => callStringSort(a, b, 'uploadedByLabel')"
        min-width="125">
        <template v-slot="props">
          {{ props.row.uploadedByLabel }}
        </template>
      </el-table-column>
      <el-table-column
        label="Last Used"
        sortable
        :sort-method="(a, b) => callDateSort(a, b, 'lastUsedAt')"
        min-width="125">
        <template v-slot="props">
          {{ props.row.lastUsedAt | formatDate }}
        </template>
      </el-table-column>
      <el-table-column
        width="140"
        fixed="right"
        label="Actions">
        <template v-slot="props">
          <p-button type="success" size="sm" icon class="btn-left">
            <i class="fa fa-info" @click="showFormDetails(props.row)"></i>
          </p-button>
          <p-button type="info" size="sm" icon @click="downloadFile(props.row)">
            <i class="fa fa-download"></i>
          </p-button>
          <p-button type="danger" size="sm" icon v-if="canEdit" @click="confirmDelete(props.row)">
            <i class="fa fa-times"></i>
          </p-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="col-sm-6 pagination-info" style="text-align: left;">
      <span class="category">Showing {{total}} entries</span>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import PButton from 'src/components/UIComponents/Button.vue'
  import {Tabs, TabPane, Card} from 'src/components/UIComponents'
  import {
    notifySuccess,
    notifyError,
    autocompleteReady,
    notifyWarning,
    stringSort,
    dateSort,
    setIndexColumnWidth,
    getTableHeight,
    verifyAutocompleteSelected,
    autocompleteTextButNoValue,
    autocompleteTextButNoValueMessage
  } from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import {OK, NO_CONTENT, UNAUTHORIZED, NOT_FOUND} from 'http-status-codes'

  export default {
    components: {
      Card,
      PButton,
      Tabs,
      TabPane
    },
    computed: {
      ...mapGetters({
        planNames: 'planNames/autocompleteResults',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder',
        tableData: 'files/energyPlans/results'
      }),
      total() {
        return this.tableData.length
      },
      canSearch() {
        return this.$can('read', 'filesSearch')
      },
      canEdit() {
        return this.$can('edit', 'files')
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.tableData.length)
      }
    },
    data () {
      return {
        toDelete: null,
        searchQuery: '',
        selectedPlanName: null,
        selectedBuilder: {id: null, name: null},
        constants: Constants
      }
    },
    methods: {
      downloadFile(file) {
        this.$store.dispatch('files/download', {
          useDirectFileUploadAndDownload: true,
          name: file.name,
          path: file.path
        })
      },
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`files/energyPlans/${refAndPathName}`))
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      getUserDisplayName(userId) {
        return this.$store.getters['users/displayName'](userId)
      },
      showFormDetails(file) {
        this.$eventHub.$emit('show-modal-1', 'File Details', file, '600px')
      },
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
      callDateSort(a, b, prop) {
        return dateSort(a, b, prop)
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async confirmDelete(file) {
        const response = await this.$store.dispatch('planNames/fileInUse', file)

        if (OK !== response.status) {
          notifyError(this, 'Error determining if ' + file.name + ' is in use. Unable to delete form.')
          return
        }

        let showButton = true
        let message = 'Are you sure you want to delete <strong>' + file.name + '</strong>? This will completely remove the form.'

        if (response.data.length > 0) {
          message = '<strong>' + file.name + '</strong> is used by the following plan names and cannot be deleted:'

          const usedBy = this.$store.getters['planNames/inUse']

          if (usedBy.length > 0) {

            message += '<p><ul>'
            usedBy.forEach((planName) => {
              let name = planName.builder.name + ': '

              if (planName.name && planName.number) {
                name += planName.name + ' - ' + planName.number
              } else if (planName.name) {
                name += planName.name
              } else if (planName.number) {
                name += planName.number
              }

              if (planName.hand) {
                name += ' - ' + planName.hand
              }

              message += '<li>' + name + '</li>'
            })
            message += '</ul></p>'
          }
          message += 'If you really want to delete this file, you must first remove association(s) to it in each of the plan names listed above, then come back here and delete the file.'
          showButton = false
        }

        this.toDelete = file
        const payload = {
          message,
          entity: file,
          delete: this.delete,
          showButton,
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('files/energyPlans/deleteOne', this.toDelete)
        if (NO_CONTENT === response.status) {
          notifySuccess(this, 'Energy Plan Deleted Successfully')
        } else if (NOT_FOUND === response.status) {
          notifyWarning(this, 'Unable to delete file. Energy Plan not found.')
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Energy Plans')
        } else {
          notifyError(this, 'Error Deleting Energy Plan')
        }
      },
      async fetchResults() {
        let response
        const planNameEnergyPlanId = this.$store.get('files/energyPlans/planNameEnergyPlanId')
        const builderId = this.$store.get('files/energyPlans/builderId')

        if (this.searchQuery && !planNameEnergyPlanId && !builderId) {
          response = await this.$store.dispatch('files/energyPlans/fetchResults', this.searchQuery)
        }

        if (planNameEnergyPlanId && !this.searchQuery && !builderId) {
          response = await this.$store.dispatch('files/energyPlans/fetchResultsByEnergyPlanId', planNameEnergyPlanId)
        }

        if (builderId && !this.searchQuery && !planNameEnergyPlanId) {
          response = await this.$store.dispatch('files/energyPlans/fetchResultsByBuilderId', builderId)
        }

        if (!response) {
          notifyWarning(this, 'Please select ONE search field to search by')
          return
        }

        if (response && OK === response.status && response.data.length === 0) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
      },
      async fetchAll() {
        const response = await this.$store.dispatch('files/energyPlans/fetchAll')
        if (OK !== response.status) {
          notifyError(this, 'Failed to load all energy plans')
        }
      },
      clearResults() {
        this.selectedPlanName = null
        this.selectedBuilder = {id: null, name: null}
        this.searchQuery = null
        this.$store.commit('companies/resetAutocompleteResults')
        this.$store.commit('files/energyPlans/resetResults')
        this.$store.set('files/energyPlans/planNameId', null)
        this.$store.set('files/energyPlans/planNameEnergyPlanId', null)
        this.$store.set('files/energyPlans/builderId', null)
      },
      async findPlanNames(text, cb) {
        this.$store.set('files/energyPlans/planNameId', null)
        this.$store.set('files/energyPlans/planNameEnergyPlanId', null)
        if (autocompleteReady(text)) {
          await this.$store.dispatch('planNames/fetchByNameOrNumberStartsWithEnergyPlans', text)
          cb(this.planNames)
        }
      },
      setPlanName(planName) {
        if (planName) {
          this.selectedPlanName = planName.name
          this.$store.set('files/energyPlans/planNameId', planName.id)
          this.$store.set('files/energyPlans/planNameEnergyPlanId', planName.energyPlanId)
        }
      },
      verifyPlanNameAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'files/energyPlans/planNameId', 'plan name', this.selectedPlanName)
      },
      async findBuilders(text, cb) {
        this.$store.set('files/energyPlans/builderId', null)
        if (autocompleteReady(text)) {
          const payload = {
            type: Constants.COMPANY.TYPES.BUILDER,
            name: text
          }

          await this.$store.dispatch('companies/fetchByNameStartsWithAndType', payload)
          if (Array.isArray(this.autocompleteBuilders)) {
            cb(this.autocompleteBuilders)
          }
        }
      },
      verifyBuilderAutocompleteSelected() {
        if (this.selectedBuilder && this.selectedBuilder.name) {
          verifyAutocompleteSelected(this, 'files/energyPlans/builderId', 'builder', this.selectedBuilder)
        }
      },
      setBuilder(builder) {
        if (builder) {
          this.selectedBuilder = builder
          this.$store.set('files/energyPlans/builderId', builder.id)
          this.$store.set('companies/autocompleteResultsBuilder', [])
        }
      }
    },
    mounted() {
      this.$eventHub.$on('energyPlanUploaded', (data) => {
        if (Array.isArray(data)) {
          this.searchQuery = data[0].name
          this.selectedBuilder.name = null
          this.selectedPlanName = null
          this.fetchResults()
        }
      }) // only fat arrow syntax works for some reason
    },
    destroyed() {
      this.$eventHub.$off('energyPlanUploaded')
      this.clearResults()
    },
    watch: {
      'selectedBuilder.name': {
        handler: function (name) {
          if (!name) {
            this.selectedBuilder.id = null
            this.$store.set('files/energyPlans/builderId',null)
          }
        }
      },
      'selectedPlanName': {
        handler: function (selectedPlanName) {
          if (!selectedPlanName) {
            this.$store.set('files/energyPlans/planNameId', null)
            this.$store.set('files/energyPlans/planNameEnergyPlanId', null)
          }
        }
      }
    }
  }
</script>
